import React from "react";
import { 
  FileText,
  Scale,
  AlertCircle,
  CheckCircle,
  XCircle,
  RefreshCw,
  CreditCard,
  Users,
  Server,
  Globe,
  Lock,
  Mail,
  Phone,
  MapPin,
  Award,
  Shield,
  Zap,
  Ban,
  Gavel,
  Briefcase
} from "lucide-react";

function TermsConditions() {
  const sections = [
    {
      id: "agreement",
      title: "1. Agreement",
      icon: FileText,
      content: "These Terms & Conditions ('Terms') govern your use of the YES CHARGE app and Services. By using YES CHARGE, you agree to these Terms and our Privacy Policy. If you do not agree, do not use the Services."
    },
    {
      id: "services",
      title: "2. Services Provided",
      icon: Zap,
      content: "YES CHARGE provides digital products and related services, including Mobile Top-up and Bundles, Games, and Social Media gifts and credits. Services may be offered to B2C users and B2B users subject to additional rules."
    },
    {
      id: "eligibility",
      title: "3. Eligibility and Accounts",
      icon: Users,
      content: "You must be legally able to form a contract in your jurisdiction. You are responsible for maintaining account confidentiality and all activities under your account. You must provide accurate information and update it when needed. We may suspend accounts involved in fraud, abuse, or policy violations."
    },
    {
      id: "prices",
      title: "4. Prices, Fees, and Taxes",
      icon: CreditCard,
      content: "Prices and availability may change due to supplier/operator changes. For B2B, commissions/discounts and settlement terms are defined in your merchant agreement or as displayed in the platform. You are responsible for applicable taxes/charges required by law."
    },
    {
      id: "orders",
      title: "5. Orders and Delivery",
      icon: RefreshCw,
      content: "Delivery of digital products depends on third-party systems (telecom operators, distributors, social platforms). We will provide a transaction reference/receipt upon completion. You must enter correct details (e.g., phone number, account/ID). Wrong inputs may result in irreversible delivery."
    },
    {
      id: "refunds",
      title: "6. Refunds, Reversals, and Failed Transactions (Digital Products)",
      icon: RefreshCw,
      subsections: [
        {
          title: "A) Successful Transactions (Delivered)",
          content: "Once a top-up/bundle/game/social credit is successfully delivered, it is generally non-refundable."
        },
        {
          title: "B) Failed Transactions",
          content: "If a transaction is marked FAILED and no value is delivered, YES CHARGE will either: automatically reverse/refund (where system rules allow), or process a reversal after verification within [24–72] hours."
        },
        {
          title: "C) Pending/Delayed Transactions",
          content: "If a transaction is PENDING, YES CHARGE will investigate with the relevant partner/operator. If delivery is not completed within a reasonable time or is confirmed failed, YES CHARGE will process a reversal/refund according to our procedures."
        },
        {
          title: "D) Incorrect Details Entered by User",
          content: "If the user enters an incorrect mobile number/account identifier and delivery completes successfully, the transaction may be irreversible and not eligible for refund."
        },
        {
          title: "E) Fraud, Abuse, or Chargeback Risk",
          content: "YES CHARGE may deny refunds or delay refunds where fraud, misuse, or dispute abuse is suspected, until the investigation is completed."
        }
      ]
    },
    {
      id: "chargebacks",
      title: "7. Chargebacks, Disputes, and Misuse of Payment Instruments",
      icon: AlertCircle,
      content: "YES CHARGE provides digital goods and services (including mobile top-ups, bundles, game credits, and social media credits). If a user completes a large-value purchase and later initiates a chargeback or disputes the transaction with their bank/card issuer after the digital value has been delivered, this may be treated as suspected misuse or fraud.",
      actions: [
        "temporarily restrict the account, apply transaction limits, or suspend access pending investigation",
        "request additional verification and supporting evidence",
        "share relevant transaction evidence with the bank or payment partner (e.g., transaction reference, timestamps, delivery confirmation, device/IP logs, and in-app activity records) to support investigation",
        "recover losses through lawful means, including offsetting against any balances/settlements (where applicable) or pursuing claims as permitted by UAE law and payment network rules"
      ],
      note: "Users must not use stolen, unauthorized, or third-party payment instruments. Any suspected fraudulent activity may be reported to relevant authorities and partners where required."
    },
    {
      id: "user-conduct",
      title: "8. User Conduct",
      icon: Ban,
      items: [
        "use the Services for unlawful purposes or to facilitate fraud",
        "attempt to bypass security controls, manipulate pricing, or exploit system vulnerabilities",
        "use unauthorized payment methods, stolen cards, or third-party accounts without permission",
        "perform repeated high-risk patterns (e.g., unusual high-value top-ups followed by disputes/chargebacks)",
        "create multiple accounts to abuse promotions, limits, or referral programs",
        "interfere with system availability, attempt reverse engineering, or disrupt Services"
      ],
      note: "May apply limits, require additional verification, suspend accounts, or refuse service where necessary for risk management and compliance."
    },
    {
      id: "b2b",
      title: "9. B2B (Merchants/Agents) Additional Terms",
      icon: Briefcase,
      content: "Merchants/agents must comply with operational policies, settlement rules, and limits set by YES CHARGE. You are responsible for safeguarding credentials. YES CHARGE may adjust limits/controls for risk management. Commission/discount structures may be updated with notice or as per contract."
    },
    {
      id: "availability",
      title: "10. Service Availability and Maintenance",
      icon: Server,
      content: "We aim for high availability but cannot guarantee uninterrupted service. Planned maintenance may occur, and emergency maintenance may be required to protect stability and security."
    },
    {
      id: "third-party",
      title: "11. Third-Party Services",
      icon: Globe,
      content: "YES CHARGE relies on third-party providers and partners. We are not responsible for outages or changes caused solely by those third parties, but we will support investigation and resolution through our processes."
    },
    {
      id: "intellectual-property",
      title: "12. Intellectual Property",
      icon: Award,
      content: "The app, branding, and content are owned by YES CHARGE or its licensors. You may not copy, modify, or distribute without permission."
    },
    {
      id: "liability",
      title: "13. Limitation of Liability",
      icon: Shield,
      content: "To the extent permitted by law, YES CHARGE is not liable for indirect, incidental, or consequential damages. YES CHARGE's total liability for a claim is limited to the amount paid for the specific transaction that caused the claim (or another limit you choose), except where prohibited by law."
    },
    {
      id: "indemnity",
      title: "14. Indemnity",
      icon: Gavel,
      content: "You agree to indemnify and hold YES CHARGE harmless from claims arising from your misuse of the Services, violation of these Terms, or violation of laws/third-party rights."
    },
    {
      id: "termination",
      title: "15. Termination",
      icon: XCircle,
      content: "We may suspend or terminate your access if you violate these Terms, or if required for security, compliance, or risk reasons."
    },
    {
      id: "governing-law",
      title: "16. Governing Law and Dispute Resolution",
      icon: Scale,
      content: "These Terms are governed by the laws of the United Arab Emirates (UAE). Any dispute arising out of or relating to the Services shall first be raised through our customer support channel for resolution. If unresolved, disputes shall be subject to the exclusive jurisdiction of the courts of [Dubai], unless applicable law requires otherwise."
    },
    {
      id: "changes",
      title: "17. Changes to Terms",
      icon: FileText,
      content: "We may update these Terms from time to time. Continued use of the Services after updates means you accept the revised Terms."
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-200">
      <div className="bg-gradient-to-r from-secondary-600 to-primary-600 dark:from-secondary-700 dark:to-primary-700 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex p-4 bg-white/20 rounded-full mb-4 backdrop-blur-sm">
            <FileText size={32} className="text-white" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4 text-white">Terms & Conditions</h1>
          <div className="flex justify-center gap-4 mt-4">
            <p className="text-white/90 text-lg">
              Effective Date: 6-Mar-2026 | Last Updated: 6-Mar-2026
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="space-y-8">
          {sections.map((section, index) => {
            const Icon = section.icon;
            return (
              <div key={index} id={section.id} className="bg-white dark:bg-gray-800 rounded-xl shadow-md dark:shadow-lg overflow-hidden transition-colors duration-200">
                <div className="bg-gradient-to-r from-gray-50 to-white dark:from-gray-800 dark:to-gray-800 px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-secondary-100 dark:bg-secondary-900/30 rounded-lg">
                      <Icon className="text-secondary-600 dark:text-secondary-400" size={20} />
                    </div>
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white">{section.title}</h2>
                  </div>
                </div>
                
                <div className="px-6 py-5">
                  {section.content && (
                    <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                      {section.content}
                    </p>
                  )}
                  
                  {section.items && (
                    <ul className="space-y-2 mb-4">
                      {section.items.map((item, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-gray-700 dark:text-gray-300">
                          <div className="flex-shrink-0 w-1.5 h-1.5 bg-secondary-600 dark:bg-secondary-400 rounded-full mt-2"></div>
                          <span>Not to {item}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  
                  {section.actions && (
                    <div className="mb-4">
                      <p className="text-gray-700 dark:text-gray-300 mb-2">In such cases, YES CHARGE may:</p>
                      <ul className="space-y-2 ml-4">
                        {section.actions.map((action, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-gray-700 dark:text-gray-300">
                            <div className="flex-shrink-0 w-1.5 h-1.5 bg-secondary-600 dark:bg-secondary-400 rounded-full mt-2"></div>
                            <span>{action}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {section.subsections && section.subsections.map((subsection, idx) => (
                    <div key={idx} className="mb-4 last:mb-0">
                      <h3 className="font-semibold text-gray-900 dark:text-white mb-2">{subsection.title}</h3>
                      <p className="text-gray-700 dark:text-gray-300">{subsection.content}</p>
                    </div>
                  ))}
                  
                  {section.note && (
                    <p className="text-sm text-gray-500 dark:text-gray-400 italic mt-2 border-l-2 border-secondary-300 dark:border-secondary-600 pl-3">
                      {section.note}
                    </p>
                  )}
                </div>
              </div>
            );
          })}

          {/* Contact Section */}
          <div className="bg-gradient-to-r from-secondary-50 to-purple-50 dark:from-secondary-900/20 dark:to-purple-900/20 rounded-xl p-8 border border-secondary-100 dark:border-secondary-800 transition-colors duration-200">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
              <Mail className="text-secondary-600 dark:text-secondary-400" size={24} />
              18. Contact
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">QUICKIE TOP – F.Z.E - Limited</h3>
                <div className="space-y-2 text-gray-700 dark:text-gray-300">
                  <div className="flex items-start gap-2">
                    <MapPin size={18} className="text-secondary-600 dark:text-secondary-400 flex-shrink-0 mt-0.5" />
                    <span>B.C. 1301499, Ajman Free Zone C1 Building, Ajman, United Arab Emirates</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Mail size={18} className="text-secondary-600 dark:text-secondary-400" />
                    <a href="mailto:support@Yes-Charge.com" className="text-secondary-600 dark:text-secondary-400 hover:underline">
                      support@Yes-Charge.com
                    </a>
                  </div>
                  <div className="flex items-center gap-2">
                    <Phone size={18} className="text-secondary-600 dark:text-secondary-400" />
                    <span>+1 (279) 790-1187</span>
                  </div>
                </div>
              </div>
              
              <div className="bg-white dark:bg-gray-800 rounded-lg p-4 transition-colors duration-200">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  For any questions about these Terms & Conditions, please contact our support team.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TermsConditions;